import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FiltersTabs,
  IconButton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from './ui';
import {
  AdjustmentsHorizontalIcon,
  BoltIcon,
  LinkIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type FilterKey = 'all' | 'active' | 'inactive';

interface TelegramLink {
  id: number;
  user_id: number;
  telegram_user_id: string;
  telegram_username?: string;
  is_active: number;
  created_at: string;
  username: string;
  role: string;
}

interface AvailableUser {
  id: number;
  username: string;
  role: string;
}

interface BotSettings {
  telegramEnabled: boolean;
  telegramBotToken: string;
  telegramChatId: string;
  telegramWebhookUrl: string;
  autoMatchNoteTemplate: string;
  manualApproveNoteTemplate: string;
  manualRejectNoteTemplate: string;
}

const filterLabels: Record<FilterKey, string> = {
  all: 'Все',
  active: 'Активные',
  inactive: 'Неактивные',
};

export default function TelegramLinks() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [telegramId, setTelegramId] = useState<string>('');
  const [botSettings, setBotSettings] = useState<BotSettings>({
    telegramEnabled: false,
    telegramBotToken: '',
    telegramChatId: '',
    telegramWebhookUrl: '',
    autoMatchNoteTemplate: 'Auto-matched with transaction {providerTxId}',
    manualApproveNoteTemplate: 'Approved by {role} {username}',
    manualRejectNoteTemplate: 'Rejected by {role} {username}',
  });
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');

  const linksQuery = useQuery<TelegramLink[]>({
    queryKey: ['telegram-links'],
    queryFn: async () => {
      const { data } = await api.get('/admin/telegram-links');
      return (data?.links || []) as TelegramLink[];
    },
  });

  const availableUsersQuery = useQuery<AvailableUser[]>({
    queryKey: ['telegram-available-users'],
    queryFn: async () => {
      const { data } = await api.get('/admin/available-users');
      return (data?.users || []) as AvailableUser[];
    },
  });

  const botSettingsQuery = useQuery<BotSettings>({
    queryKey: ['telegram-bot-settings'],
    queryFn: async () => {
      const { data } = await api.get('/admin/shop/settings');
      return {
        telegramEnabled: data.telegramEnabled || false,
        telegramBotToken: data.telegramBotToken || '',
        telegramChatId: data.telegramChatId || '',
        telegramWebhookUrl: data.telegramWebhookUrl || '',
        autoMatchNoteTemplate: data.autoMatchNoteTemplate || 'Auto-matched with transaction {providerTxId}',
        manualApproveNoteTemplate: data.manualApproveNoteTemplate || 'Approved by {role} {username}',
        manualRejectNoteTemplate: data.manualRejectNoteTemplate || 'Rejected by {role} {username}',
      } as BotSettings;
    },
  });

  useEffect(() => {
    if (botSettingsQuery.data) {
      setBotSettings(botSettingsQuery.data);
    }
  }, [botSettingsQuery.data]);

  const addLinkMutation = useMutation({
    mutationFn: async ({ userId, telegramUserId }: { userId: number; telegramUserId: string }) => {
      const { data } = await api.post('/admin/telegram-links', { userId, telegramUserId });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegram-links'] });
      queryClient.invalidateQueries({ queryKey: ['telegram-available-users'] });
      setShowAddForm(false);
      setSelectedUserId('');
      setTelegramId('');
      setError('');
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || 'Ошибка создания связи');
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: number) => {
      await api.delete(`/admin/telegram-links/${linkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegram-links'] });
      queryClient.invalidateQueries({ queryKey: ['telegram-available-users'] });
    },
  });

  const saveBotSettingsMutation = useMutation({
    mutationFn: async (payload: BotSettings) => {
      await api.post('/admin/shop/notification-settings', payload);
    },
    onSuccess: () => {
      alert('Настройки бота сохранены');
    },
    onError: () => {
      alert('Ошибка сохранения настроек');
    },
  });

  const links = linksQuery.data ?? [];
  const availableUsers = availableUsersQuery.data ?? [];
  const isLoading = linksQuery.isLoading || availableUsersQuery.isLoading || botSettingsQuery.isLoading;

  const filteredLinks = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return links.filter((link) => {
      const matchesFilter =
        filter === 'all' || (filter === 'active' && link.is_active) || (filter === 'inactive' && !link.is_active);
      const matchesSearch =
        !normalizedSearch ||
        link.username.toLowerCase().includes(normalizedSearch) ||
        (link.telegram_username ?? '').toLowerCase().includes(normalizedSearch) ||
        link.telegram_user_id.includes(normalizedSearch);
      return matchesFilter && matchesSearch;
    });
  }, [links, filter, search]);

  const filterTabs = useMemo(() => {
    const counts = links.reduce<Record<FilterKey, number>>(
      (acc, link) => {
        acc.all += 1;
        if (link.is_active) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { all: 0, active: 0, inactive: 0 }
    );
    return (Object.keys(filterLabels) as FilterKey[]).map((key) => ({ key, label: filterLabels[key], badge: counts[key] }));
  }, [links]);

  const handleAddLink = () => {
    if (!selectedUserId || !telegramId) {
      setError('Выберите пользователя и введите Telegram ID');
      return;
    }
    addLinkMutation.mutate({ userId: parseInt(selectedUserId, 10), telegramUserId: telegramId });
  };

  const handleDeleteLink = (linkId: number) => {
    if (!confirm('Удалить эту связь?')) return;
    deleteLinkMutation.mutate(linkId);
  };

  const handleSaveBotSettings = () => {
    saveBotSettingsMutation.mutate(botSettings);
  };

  if (isLoading) {
    return <div className="text-center py-8">Загрузка...</div>;
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader
          title="Telegram-бот"
          description="Подключите бота, чтобы получать уведомления о платежах и выводах."
          actions={
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-600">
              <BoltIcon className="h-4 w-4" /> Реальное время
            </span>
          }
        />
        <CardBody className="grid gap-4 lg:grid-cols-2">
          <label className="space-y-2 text-sm text-slate-600">
            <span className="text-xs font-semibold uppercase text-secondary-500">Telegram Bot Token</span>
            <input
              type="text"
              value={botSettings.telegramBotToken}
              onChange={(event) => setBotSettings((prev) => ({ ...prev, telegramBotToken: event.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <span className="text-xs text-slate-500">Получите токен у BotFather.</span>
          </label>
          <label className="space-y-2 text-sm text-slate-600">
            <span className="text-xs font-semibold uppercase text-secondary-500">Чат ID</span>
            <input
              type="text"
              value={botSettings.telegramChatId}
              onChange={(event) => setBotSettings((prev) => ({ ...prev, telegramChatId: event.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <span className="text-xs text-slate-500">Укажите группу или личный чат, куда будут приходить уведомления.</span>
          </label>
          <label className="space-y-2 text-sm text-slate-600">
            <span className="text-xs font-semibold uppercase text-secondary-500">Webhook URL</span>
            <input
              type="text"
              value={botSettings.telegramWebhookUrl}
              onChange={(event) => setBotSettings((prev) => ({ ...prev, telegramWebhookUrl: event.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase text-secondary-500">Шаблоны сообщений</p>
            <textarea
              value={botSettings.autoMatchNoteTemplate}
              onChange={(event) => setBotSettings((prev) => ({ ...prev, autoMatchNoteTemplate: event.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              rows={2}
            />
            <textarea
              value={botSettings.manualApproveNoteTemplate}
              onChange={(event) => setBotSettings((prev) => ({ ...prev, manualApproveNoteTemplate: event.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              rows={2}
            />
            <textarea
              value={botSettings.manualRejectNoteTemplate}
              onChange={(event) => setBotSettings((prev) => ({ ...prev, manualRejectNoteTemplate: event.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              rows={2}
            />
          </div>
          <div className="lg:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-surface-subtle px-4 py-3 text-xs text-slate-600">
            <span className="inline-flex items-center gap-2">
              <ShieldCheckIcon className="h-4 w-4 text-success" />
              Бот {botSettings.telegramEnabled ? 'активирован' : 'отключен'}. Используйте шаблоны для кастомизации сообщений.
            </span>
            <button
              onClick={handleSaveBotSettings}
              disabled={saveBotSettingsMutation.isPending}
              className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saveBotSettingsMutation.isPending ? 'Сохранение...' : 'Сохранить настройки'}
            </button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          title={<span className="text-lg font-semibold text-slate-900">Привязанные пользователи</span>}
          actions={<FiltersTabs tabs={filterTabs} value={filter} onChange={(key) => setFilter(key as FilterKey)} />}
          description="Подключите операторов к боту, чтобы они получали уведомления."
        />
        <CardBody className="space-y-4">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по оператору или Telegram ID"
              className="w-full border-none bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
            <button
              onClick={() => {
                setShowAddForm(true);
                setError('');
              }}
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white shadow-soft transition hover:bg-primary-600"
            >
              <PlusIcon className="h-4 w-4" /> Добавить связь
            </button>
          </div>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Пользователь</TableHeaderCell>
                  <TableHeaderCell>Telegram</TableHeaderCell>
                  <TableHeaderCell>Создан</TableHeaderCell>
                  <TableHeaderCell numeric>Действия</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLinks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <EmptyState
                        title="Связей пока нет"
                        description="Подключите пользователей, чтобы они получали уведомления в личный Telegram."
                        icon={<LinkIcon className="h-7 w-7" />}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLinks.map((link) => (
                    <TableRow key={link.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary-50 text-primary-500">
                            {link.username.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="space-y-1 text-sm">
                            <p className="font-semibold text-slate-900">{link.username}</p>
                            <p className="text-xs text-slate-500">Роль: {link.role}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          <p className="font-medium text-slate-800">{link.telegram_username || '—'}</p>
                          <p className="text-xs text-slate-500">ID: {link.telegram_user_id}</p>
                          <StatusBadge tone={link.is_active ? 'success' : 'default'}>
                            {link.is_active ? 'Активно' : 'Отключено'}
                          </StatusBadge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-600">{new Date(link.created_at).toLocaleString('ru-RU')}</div>
                      </TableCell>
                      <TableCell numeric>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleDeleteLink(link.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-danger transition hover:border-danger/30"
                          >
                            <TrashIcon className="h-4 w-4" /> Удалить
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {showAddForm ? (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-slate-900/40" onClick={() => setShowAddForm(false)} />
          <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-lifted">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Новая связь</p>
                <h2 className="text-lg font-semibold text-slate-900">Подключение Telegram</h2>
              </div>
              <IconButton aria-label="Закрыть" onClick={() => setShowAddForm(false)}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-6 text-sm text-slate-600">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase text-secondary-500">Пользователь</span>
                <select
                  value={selectedUserId}
                  onChange={(event) => setSelectedUserId(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <option value="">Выберите пользователя</option>
                  {availableUsers.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.username} ({item.role})
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase text-secondary-500">Telegram ID</span>
                <input
                  type="text"
                  value={telegramId}
                  onChange={(event) => setTelegramId(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  placeholder="Например, 123456789"
                />
              </label>
              {error ? <div className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">{error}</div> : null}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setError('');
                  setSelectedUserId('');
                  setTelegramId('');
                }}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary-200"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleAddLink}
                disabled={addLinkMutation.isPending}
                className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {addLinkMutation.isPending ? 'Добавление...' : 'Добавить связь'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
